import { FC } from "react";
import {
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldSubmit,
  PillItem,
} from "../../../components/admin/formFields";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  REPORT_ADVANCED_FILTER_PARAM_NAME,
  REPORT_GROUP_BY_LEVEL_SLUGS,
} from "../constants";
import { Droppable } from "@hello-pangea/dnd";
import { useCreateCustomReportContext } from "../context/createCustomReport.context";
import { APP_CONFIG } from "../../../constants/constants";
import { useTranslation } from "react-i18next";

type TAdvancedFiltersBarProps = {
  wrapperClass?: string;
  onResetAdvancedFilter?: () => void;
};

const AdvancedFiltersBar: FC<TAdvancedFiltersBarProps> = ({
  wrapperClass = "border-b-2 border-t-2 border-gray-300 py-3",
  onResetAdvancedFilter,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.advance_filters",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const {
    groupByColumns,
    setGroupByColumns,
    subGroupByColumns,
    setSubGroupByColumns,
    specificReportResponse,
  } = useCreateCustomReportContext();

  const activeAdvancedFilters = specificReportResponse?.report_filters?.find(
    (filter) => filter.param === REPORT_ADVANCED_FILTER_PARAM_NAME
  );

  return (
    <div className={wrapperClass}>
      <div className="flex w-full gap-4 ">
        {/* Group By Section */}
        <div className="flex gap-2 items-start w-1/3 space-x-2">
          <label className="shrink-0 w-16 text-sm font-medium h-12 flex items-center">
            Group by
          </label>
          <Droppable droppableId={REPORT_GROUP_BY_LEVEL_SLUGS.GROUP_BY}>
            {(provided, snapshot) => (
              <div
                className={`relative flex-grow min-h-12 rounded-lg ${snapshot.isDraggingOver ? " bg-white ring-2 ring-accent-blue-bright" : "bg-gray-200"}`}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                  <pre className="absolute right-full bg-white/80 text-[8px] leading-[8px] z-modal w-36 max-h-36 overflow-auto">
                    {JSON.stringify(groupByColumns, null, 2)}
                  </pre>
                )}
                <AdminFormFieldPillList
                  heightClass="min-h-12"
                  paddingClass="p-2"
                >
                  {groupByColumns.map((column, index) => (
                    <AdminFormFieldPillItem
                      key={`group-by-pill-${column.id}`}
                      item={
                        {
                          name:
                            column?.header || column?.identifier || column?.id,
                        } as PillItem
                      }
                      onRemove={() => {
                        const newColumns = [...groupByColumns];
                        newColumns.splice(index, 1);
                        // if newColumns is empty, move the first item from subGroupByColumns to groupByColumns
                        if (
                          newColumns.length === 0 &&
                          subGroupByColumns.length > 0
                        ) {
                          newColumns.push(subGroupByColumns[0]);
                          const newSubGroupByColumns = [...subGroupByColumns];
                          newSubGroupByColumns.splice(0, 1);
                          setSubGroupByColumns(newSubGroupByColumns);
                        }
                        // set the new groupByColumns
                        setGroupByColumns(newColumns);
                      }}
                      disabled={false}
                    />
                  ))}
                  {provided.placeholder}
                </AdminFormFieldPillList>
              </div>
            )}
          </Droppable>
        </div>

        {/* Sub-Group By Section */}
        <div className="flex gap-2 flex-1 items-center space-x-2">
          <label className="shrink-0 w-24 text-sm font-medium h-12 flex items-center">
            Sub-group by
          </label>
          <Droppable droppableId={REPORT_GROUP_BY_LEVEL_SLUGS.SUB_GROUP_BY}>
            {(provided, snapshot) => (
              <div
                className={`relative flex-grow min-h-12 rounded-lg ${snapshot.isDraggingOver ? " bg-white ring-2 ring-accent-blue-bright" : "bg-gray-200"}`}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                  <pre className="absolute right-full bg-white/80 text-[8px] leading-[8px] z-modal w-36 max-h-36 overflow-auto">
                    {JSON.stringify(subGroupByColumns, null, 2)}
                  </pre>
                )}
                <AdminFormFieldPillList
                  heightClass="min-h-12"
                  paddingClass="p-2"
                >
                  {subGroupByColumns.map((column, index) => (
                    <AdminFormFieldPillItem
                      key={`sub-group-by-pill-${column.id}`}
                      item={
                        {
                          name:
                            column?.header || column?.identifier || column?.id,
                        } as PillItem
                      }
                      onRemove={() => {
                        const newColumns = [...subGroupByColumns];
                        newColumns.splice(index, 1);
                        setSubGroupByColumns(newColumns);
                      }}
                      disabled={false}
                    />
                  ))}
                  {provided.placeholder}
                </AdminFormFieldPillList>
              </div>
            )}
          </Droppable>
        </div>
      </div>

      {/* Advanced Filter Buttons */}
      <div className="mt-4 flex space-x-4 items-center">
        <label className="text-sm font-medium ">
          {t("label_advance_filter")}
        </label>
        <AdminFormFieldSubmit
          variant="primary-like"
          label={t(!!activeAdvancedFilters ? "button_update" : "button_create")}
          buttonDimensionClass="px-3 py-2"
          onClick={() => {
            dispatch(
              setModalsData({
                ...modalsState,
                showModalNewCustomReportStep1: false,
                showModalNewCustomReportStep2: false,
                showModalNewCustomReportStep3: false,
                showAdvanceFilterPopup: true,
              })
            );
          }}
        />
        <AdminFormFieldSubmit
          variant="primary-alt"
          label={t("button_reset")}
          buttonDimensionClass="px-4 py-2"
          disabled={!activeAdvancedFilters}
          onClick={onResetAdvancedFilter}
        />
      </div>
    </div>
  );
};

export default AdvancedFiltersBar;
